import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { BiBlendJoin } from '@euler/model/bi/blend';
import { BiConformedKey, BiDatasetCatalog } from '@euler/model/bi/catalog';
import {
  BiAggregation,
  BiDatasetRef,
  BiDatasetRefKind,
  BiFilter,
  BiFilterOp,
} from '@euler/model/bi/query';
import { biLabelledCell } from '@euler/model/bi/result';

export const kBiAggregationLabels: Record<BiAggregation, string> = {
  [BiAggregation.Sum]: '合计',
  [BiAggregation.Avg]: '平均',
  [BiAggregation.Min]: '最小',
  [BiAggregation.Max]: '最大',
  [BiAggregation.Count]: '计数',
  [BiAggregation.CountDistinct]: '去重计数',
};

export const kBiFilterOpLabels: Record<BiFilterOp, string> = {
  [BiFilterOp.Eq]: '等于',
  [BiFilterOp.Neq]: '不等于',
  [BiFilterOp.Gt]: '大于',
  [BiFilterOp.Gte]: '不小于',
  [BiFilterOp.Lt]: '小于',
  [BiFilterOp.Lte]: '不大于',
  [BiFilterOp.In]: '属于',
  [BiFilterOp.NotIn]: '不属于',
  [BiFilterOp.Between]: '介于',
  [BiFilterOp.Contains]: '包含',
  [BiFilterOp.IsNull]: '为空',
  [BiFilterOp.NotNull]: '不为空',
};

export const kBiConformedKeyLabels: Record<BiConformedKey, string> = {
  [BiConformedKey.Date]: '日期',
  [BiConformedKey.Store]: '门店',
  [BiConformedKey.LicensePlate]: '车牌',
  [BiConformedKey.Vin]: '车架号',
  [BiConformedKey.CustomerPhone]: '客户手机号',
  [BiConformedKey.ServiceItemCategory]: '服务项目分类',
  [BiConformedKey.Technician]: '技师',
  [BiConformedKey.ServiceAgent]: '服务顾问',
};

export const kBiBlendJoinLabels: Record<BiBlendJoin, string> = {
  [BiBlendJoin.Left]: '以第一份数据为准',
  [BiBlendJoin.Inner]: '只留两边都有的',
  [BiBlendJoin.Full]: '保留两边全部',
};

export function biSameDataset(a: BiDatasetRef | null, b: BiDatasetRef) {
  if (!a || a.kind !== b.kind) return false;
  return a.kind === BiDatasetRefKind.Sql
    ? b.kind === BiDatasetRefKind.Sql && a.model === b.model
    : b.kind === BiDatasetRefKind.Upload && a.datasetId === b.datasetId;
}

/** A field's label from the catalog, or its key when the catalog is absent. */
export function biFieldLabel(
  catalog: BiDatasetCatalog | null,
  field: string,
): string {
  const found = catalog?.fields.find(entry => entry.key === field);
  return found ? biText(found.label) : field;
}

/** One filter in words, e.g. `状态 等于 已完成`. */
export function biFilterLabel(
  catalog: BiDatasetCatalog | null,
  filter: BiFilter,
): string {
  const field = biFieldLabel(catalog, filter.field);
  const op = kBiFilterOpLabels[filter.op];
  // A coded field's values read as their labels; the filter keeps codes.
  const labels = catalog?.fields.find(entry => entry.key === filter.field)
    ?.valueLabels;
  const text = (value: string | number | boolean) =>
    String(biLabelledCell(value, labels));
  switch (filter.op) {
    case BiFilterOp.In:
    case BiFilterOp.NotIn:
      return `${field} ${op} ${filter.values.map(text).join('、')}`;
    case BiFilterOp.Between:
      return `${field} ${op} ${filter.from} – ${filter.to}`;
    case BiFilterOp.IsNull:
    case BiFilterOp.NotNull:
      return `${field} ${op}`;
    case BiFilterOp.Contains:
      return `${field} ${op} ${filter.value}`;
    default:
      return `${field} ${op} ${text(filter.value)}`;
  }
}
